import { ExternalLink } from 'lucide-react';
import type { ModelLink } from '@maker-toolchest/shared';
import { Button } from './Button';
import { DeleteButton } from './DeleteButton';
import { LinkBadge } from './LinkBadge';
import { LinkMetadataDisplay } from './LinkMetadataDisplay';
import { formatDate } from '@/utils/formatting';

interface LinkCardProps {
  link: ModelLink;
  onRetryMetadata?: (linkId: string) => void;
  onDelete?: (linkId: string) => void;
  expandedDescriptions?: Set<string>;
  onToggleDescription?: (linkId: string) => void;
  isRetrying?: boolean;
  isDeleting?: boolean;
}

export function LinkCard({
  link,
  onRetryMetadata,
  onDelete,
  expandedDescriptions = new Set(),
  onToggleDescription,
  isRetrying = false,
  isDeleting = false,
}: LinkCardProps) {
  return (
    <div className="p-4 border border-border rounded-lg bg-background hover:bg-background-subtle transition-colors">
      <div className="flex items-start gap-4">
        {/* Thumbnail */}
        {link.metadataThumbnail && (
          <div className="flex-shrink-0">
            <img
              src={link.metadataThumbnail}
              alt={link.metadataTitle || 'Model thumbnail'}
              className="w-24 h-24 object-cover rounded border border-border"
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Header with badges and status */}
          <div className="flex items-center flex-wrap gap-2 mb-2">
            <LinkBadge source={link.source} />
            {link.metadataStatus === 'PROCESSING' && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded">
                Extracting metadata...
              </span>
            )}
            {link.metadataStatus === 'COMPLETED' && link.metadataLastSync && (
              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded">
                Metadata extracted
              </span>
            )}
          </div>

          {/* Title */}
          {link.metadataTitle && (
            <h4 className="text-base font-semibold text-foreground mb-1">
              {link.metadataTitle}
            </h4>
          )}

          {/* URL */}
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline break-all flex items-center gap-1"
          >
            {link.url}
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
          </a>

          {/* Metadata (author, license, description, tags) */}
          <LinkMetadataDisplay
            author={link.metadataAuthor}
            license={link.metadataLicense}
            description={link.metadataDescription}
            tags={link.metadataTags}
            linkId={link.id}
            expandedDescriptions={expandedDescriptions}
            onToggleDescription={onToggleDescription}
          />

          {/* Error message if failed */}
          {link.metadataStatus === 'FAILED' && link.metadataError && (
            <div className="mt-2 p-2 bg-error/10 border border-error/20 rounded text-sm text-error">
              {link.metadataError}
            </div>
          )}

          {/* Notes */}
          {link.notes && (
            <div className="mt-2 text-sm text-foreground-muted">{link.notes}</div>
          )}

          {/* Metadata last synced */}
          {link.metadataLastSync && (
            <div className="mt-2 text-xs text-foreground-muted">
              Metadata last synced: {formatDate(link.metadataLastSync)}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-start gap-2">
          {link.metadataStatus === 'FAILED' && onRetryMetadata && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onRetryMetadata(link.id)}
              disabled={isRetrying}
              loading={isRetrying}
            >
              Retry
            </Button>
          )}
          {onDelete && (
            <DeleteButton
              variant="ghost"
              onClick={() => onDelete(link.id)}
              disabled={isDeleting}
              showText={false}
            />
          )}
        </div>
      </div>
    </div>
  );
}
